import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Power, Plus, Pencil, Trash2 } from 'lucide-react';
import { adminFeesOptions } from '../lib/resources/queries';
import { useToggleFee } from '../lib/resources/mutations';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { StatusPip } from '../components/ui/status-pip';
import { EmptyState } from '../components/ui/empty-state';
import { formatMoney } from '../lib/utils';
import type { FeeConfigResponse } from '../lib/resources/types';
import { FeeFormDialog } from './-components/fee-form-dialog';
import { FeeDeleteConfirmDialog } from './-components/fee-delete-confirm-dialog';

export const Route = createFileRoute('/admin/fees')({
  loader: ({ context: { queryClient } }) => queryClient.ensureQueryData(adminFeesOptions()),
  component: AdminFeesPage,
});

function AdminFeesPage() {
  const fees = useSuspenseQuery(adminFeesOptions()).data;
  const toggle = useToggleFee();
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<FeeConfigResponse | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<FeeConfigResponse | null>(null);

  return (
    <div className="max-w-4xl mx-auto p-8 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-[28px] font-bold tracking-tight">Fee config</h1>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="size-3.5" /> New fee
        </Button>
      </div>
      <Card>
        <CardHeader><CardTitle>Master-invoice fees</CardTitle></CardHeader>
        <CardContent>
          {fees.length === 0 ? (
            <EmptyState icon={Power} title="No fees configured"
              description="Add fixed fees and/or percent fees that apply on top of every master invoice." />
          ) : (
            <ul className="divide-y divide-border">
              {fees.map((f) => (
                <li key={f.id} className="flex items-center gap-4 py-3 text-[13px]">
                  <StatusPip
                    severity={f.active ? 'positive' : 'info'}
                    label={f.active ? 'ACTIVE' : 'INACTIVE'}
                  />
                  <span className="font-medium flex-1">{f.name}</span>
                  <span className="text-[11px] text-foreground-muted uppercase tracking-wide w-20">{f.type}</span>
                  <span className="font-mono w-32 text-right">
                    {f.type === 'FIXED'
                      ? formatMoney(f.amountCents ?? 0)
                      : `${((f.rateBasisPoints ?? 0) / 100).toFixed(2)}%`}
                  </span>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditTarget(f)} title="Edit">
                      <Pencil className="size-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => toggle.mutate(f.id)} title={f.active ? 'Deactivate' : 'Activate'}>
                      <Power className="size-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(f)} title="Delete">
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <FeeFormDialog open={createOpen} onOpenChange={setCreateOpen} />
      <FeeFormDialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)} fee={editTarget} />
      <FeeDeleteConfirmDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)} fee={deleteTarget} />
    </div>
  );
}
